/**
 * *Keep this drawing as a map* — `docs/property-maps-v2.md` §5.4, board 5
 * tile 4.
 *
 * A measure session is a tape, and a tape is thrown away. This is the one way
 * the outlines survive it — and it moves **only the shapes**: the numbers
 * somebody added to the property were saved as parts the moment they pressed
 * Add, and a second copy arriving because the drawing was also kept would
 * double every total on the Overview card. The dialog says so, because
 * "will this duplicate my measurements" is the question somebody has while
 * looking at this button.
 */
import { useState } from 'react'
import { Button } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { TextInput } from '../../components/form/inputs.js'
import { cn } from '../../lib/cn.js'
import { IconMaps } from '../../frame/icons.js'

export function KeepAsMapDialog({
  propertyName, shapeCount, busy, onCancel, onKeep,
}: {
  propertyName: string
  shapeCount: number
  busy?: boolean
  onCancel: () => void
  onKeep: (body: { name: string; layerMode: 'one' | 'per_type' }) => void
}) {
  const [name, setName] = useState(`${propertyName} · measured`)
  const [layerMode, setLayerMode] = useState<'one' | 'per_type'>('one')

  return (
    <SheetOrDialog
      onClose={onCancel}
      title="Keep this drawing as a map"
      width={460}
      mark={<IconMaps size={16} />}
      footer={(
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || name.trim() === ''}
            onClick={() => onKeep({ name: name.trim(), layerMode })}
          >
            {busy ? 'Making the map…' : 'Keep as a map'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <label className="flex flex-col gap-1.5">
          <span className="text-fieldlabel text-text-muted">Name</span>
          <TextInput value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </label>

        <fieldset className="flex flex-col gap-1.5 border-0 p-0">
          <legend className="p-0 text-fieldlabel text-text-muted">
            {`Put the ${shapeCount} shape${shapeCount === 1 ? '' : 's'} on`}
          </legend>
          <Choice
            label="One layer"
            body="Everything on a layer called Measurements."
            on={layerMode === 'one'}
            onPick={() => setLayerMode('one')}
          />
          <Choice
            label="A layer per type"
            body="Turf on one, beds on another — named after the measurement type."
            on={layerMode === 'per_type'}
            onPick={() => setLayerMode('per_type')}
          />
        </fieldset>

        <p className="m-0 text-cell leading-relaxed text-text-muted">
          Layers start as All crews. Narrow them next.
          <br />
          The numbers you added to the property stay as they are.
        </p>
      </div>
    </SheetOrDialog>
  )
}

function Choice({ label, body, on, onPick }: {
  label: string; body: string; on: boolean; onPick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={on}
      className={cn(
        'flex items-start gap-2.5 rounded-field border p-3 text-left',
        on ? 'border-accent-line bg-accent-soft' : 'border-line hover:bg-hover',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2',
          on ? 'border-accent bg-accent' : 'border-line-strong',
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <b className="text-cell">{label}</b>
        <span className="text-sub text-text-muted">{body}</span>
      </span>
    </button>
  )
}
